/* eslint-disable jsx-a11y/anchor-is-valid */

import { Stomp } from '@stomp/stompjs'
import { FC, useEffect, useId, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { useStopwatch } from 'react-timer-hook'
import { AtendimentoDtoAnotacao, AtendimentoView } from '../../atendimento/core/_models'
import {
  AnotacaoType,
  AtendimentoEstado,
  getWebSocketUrl,
  IconPaths,
  ModalidadeType,
  showAlert,
  showConfirmAlert,
  showError,
  toAbsoluteUrl,
  KTSVG,
} from '../../../../../_metronic/helpers'
import { adicionarAnotacao, getAtendimentoById } from '../../atendimento/core/_requests'
import { useAuth } from '../../../auth'
import { Button, InfoContainer, LoadingIndicator, TextEditor } from '../../../../components'
import { AsideMenuAtendimentoMain } from './aside/AsideMenuAtendimentoMain'
import styles from './aside/styles/aside-atendimento.module.css'
import { InformacoesAdicionaisBanner } from './components/InformacoesAdicionaisBanner'
import { formatarRelogio, formatarRelogioCompacto } from './core/formatacaoTempo'
import { usePersistedHeight } from './hooks/usePersistedHeight'
import { VideoAtendimentoAgora } from './video-atendimento-agora'
import { Link } from 'react-router-dom'
import { useT } from '_metronic/i18n/Metronici18n'
import { InfoAlert } from '../../../../components/Info/InfoAlert'
import { useChamadaAtiva } from '../chamada-ativa/ChamadaAtivaProvider'
import { useSessaoAtendimento } from '../chamada-ativa/useSessaoAtendimento'
import { EncaixeChamada } from '../chamada-ativa/components/EncaixeChamada'
import { rotaDaChamada, videoAgoraHabilitado } from '../chamada-ativa/core/_models'

const MODALIDADE_LABEL: Record<string, string> = {
  TELEKIT: 'Telekit',
  PRESENCIAL: 'Presencial',
  REMOTO: 'Teleconsulta',
  PARECER: 'Parecer',
}

const calcularIdade = (dataNascimento?: string) => {
  if (!dataNascimento) return undefined
  const hoje = new Date()
  const nascimento = new Date(dataNascimento)
  let idade = hoje.getFullYear() - nascimento.getFullYear()
  const mesAtual = hoje.getMonth()
  const mesNascimento = nascimento.getMonth()
  if (
    mesAtual < mesNascimento ||
    (mesAtual === mesNascimento && hoje.getDate() < nascimento.getDate())
  ) {
    idade--
  }
  return idade
}

/** Rede de segurança do aviso STOMP, cujo cliente não reconecta sozinho. */
const INTERVALO_RECARGA_SESSOES_MS = 60_000

/** O webhook publica o aviso antes de gravar a sessão no banco. */
const ESPERA_RELEITURA_APOS_AVISO_MS = 2_000

type DadosVideochamada = Pick<AtendimentoView, 'sessoesVideo' | 'tempoVideochamadaSegundos'>

const extrairDadosVideochamada = ({
  sessoesVideo,
  tempoVideochamadaSegundos,
}: AtendimentoView): DadosVideochamada => ({ sessoesVideo, tempoVideochamadaSegundos })

/**
 * Tempo desde o início do atendimento e, entre parênteses, o tempo em videochamada: sessões
 * encerradas (do backend) + o decorrido da sessão aberta.
 */
const TempoDecorrido: FC<{ inicio: string; atendimento: AtendimentoView }> = ({
  inicio,
  atendimento,
}) => {
  const idTooltip = useId()
  const offset = new Date()
  const decorrido = Math.max(0, Math.floor((offset.getTime() - new Date(inicio).getTime()) / 1000))
  offset.setSeconds(offset.getSeconds() + decorrido)

  const { days, hours, minutes, seconds } = useStopwatch({
    autoStart: true,
    offsetTimestamp: offset,
  })
  const totalSegundos = ((days * 24 + hours) * 60 + minutes) * 60 + seconds

  const idAtendimento = atendimento.id
  const estadoId = atendimento.estado?.id
  // Mesma regra do backend para "Em andamento" × "Fim não registrado".
  const emCurso =
    estadoId !== AtendimentoEstado.Finalizado &&
    estadoId !== AtendimentoEstado.Falta &&
    estadoId !== AtendimentoEstado.Cancelado

  // Isolada aqui para não religar o carregamento da tela, que derrubaria a chamada. Falha de
  // recarga não alerta: o react-query mantém o último valor e tenta de novo no próximo ciclo.
  const { auth } = useAuth()
  const { data: videochamada = extrairDadosVideochamada(atendimento), refetch } = useQuery(
    ['atendimento-sessoes-video', idAtendimento],
    () => getAtendimentoById(idAtendimento).then(extrairDadosVideochamada),
    {
      enabled: emCurso && Boolean(idAtendimento),
      initialData: () => extrairDadosVideochamada(atendimento),
      staleTime: INTERVALO_RECARGA_SESSOES_MS,
      cacheTime: 0,
      refetchInterval: INTERVALO_RECARGA_SESSOES_MS,
      refetchOnWindowFocus: false,
    }
  )

  // Em ref para a assinatura STOMP não ser refeita a cada segundo.
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  useEffect(() => {
    const accessToken = auth?.accessToken
    if (!emCurso || !idAtendimento || !accessToken) return

    let espera: number | undefined
    const stompClient = Stomp.client(getWebSocketUrl('/socket/atendimento'))
    stompClient.debug = () => undefined

    const onConnect = () => {
      stompClient.subscribe(`/topic/atendimento/online/${idAtendimento}`, () => {
        window.clearTimeout(espera)
        espera = window.setTimeout(() => refetchRef.current(), ESPERA_RELEITURA_APOS_AVISO_MS)
      })
    }

    stompClient.connect({ Authorization: `Bearer ${accessToken}` }, onConnect, () => undefined)

    return () => {
      window.clearTimeout(espera)
      try {
        stompClient.disconnect()
      } catch {}
    }
  }, [idAtendimento, emCurso, auth?.accessToken])

  const sessaoAberta = emCurso
    ? videochamada.sessoesVideo?.find((sessao) => !sessao.fim)
    : undefined
  const inicioSessaoAbertaMs = sessaoAberta ? new Date(sessaoAberta.inicio).getTime() : NaN
  const decorridoSessaoAberta = Number.isFinite(inicioSessaoAbertaMs)
    ? Math.max(0, Math.floor((Date.now() - inicioSessaoAbertaMs) / 1000))
    : 0
  const tempoVideochamada = (videochamada.tempoVideochamadaSegundos ?? 0) + decorridoSessaoAberta
  const temVideochamada = tempoVideochamada >= 1

  return (
    <div className='d-flex flex-column text-end'>
      <span className='text-muted text-uppercase fs-8 fw-bold'>Tempo</span>
      <OverlayTrigger
        placement='bottom'
        overlay={
          <Tooltip id={idTooltip}>
            Tempo de atendimento, contado desde o início do atendimento.
            {temVideochamada && ' Entre parênteses, o tempo em videochamada.'}
          </Tooltip>
        }
      >
        {/* Focável para o tooltip abrir pelo teclado. */}
        <span tabIndex={0} className='fs-7 fw-bold text-nowrap'>
          <span className='text-primary'>{formatarRelogio(totalSegundos)}</span>
          {temVideochamada && (
            <>
              {' '}
              <span className='text-gray-700'>
                (<span className='visually-hidden'>em videochamada </span>
                {formatarRelogioCompacto(tempoVideochamada)}
                <KTSVG path={IconPaths.Record} className='svg-icon-em fs-8 ms-1' ariaHidden />)
              </span>
            </>
          )}
        </span>
      </OverlayTrigger>
    </div>
  )
}

type Props = {
  atendimento: AtendimentoView
  /** Rota do profissional com player Agora: a chamada vive no provider e sobrevive à navegação. */
  chamadaPersistente?: boolean
}

export const VideoAtendimento: FC<Props> = ({ atendimento, chamadaPersistente = false }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const { currentUser } = useAuth()
  const { t } = useT()
  const chamada = useChamadaAtiva()
  const sessao = useSessaoAtendimento(Number(atendimento.id), chamadaPersistente)
  const anotacao = sessao.rascunhoAnamnese
  const anotacaoSalva = sessao.anamneseSalva
  const setAnotacao = (texto: string) => sessao.atualizar({ rascunhoAnamnese: texto })
  const [loading, setLoading] = useState(false)
  const [participantCount, setParticipantCount] = useState(1)
  const [chatOpenLocal, setChatOpenLocal] = useState(false)
  const chatOpen = chamadaPersistente ? chamada.chatAberto : chatOpenLocal
  const setChatOpen = chamadaPersistente ? chamada.setChatAberto : setChatOpenLocal
  const participantes = chamadaPersistente ? chamada.participantes : participantCount
  const { iniciarChamada } = chamada
  const idPessoa = currentUser?.pessoa?.id
  const pacienteNome = atendimento.paciente?.nome
  const protocolo = atendimento.protocolo
  // Mesmo texto do cabeçalho da tela, repassado à chamada para o cabeçalho do PiP.
  const pessoa = atendimento.paciente?.pessoa
  const idade = calcularIdade(pessoa?.dataNascimento as string | undefined)
  const sexoIdade = [pessoa?.sexo, idade != null ? `${idade} anos` : undefined]
    .filter(Boolean)
    .join(' · ')
  // Decidido já no render, nunca depois de um efeito: montar o encaixe aqui, mesmo por um instante,
  // traria para a tela deste paciente o vídeo do paciente da outra chamada (LGPD).
  const chamadaAtivaDeOutroAtendimento =
    chamadaPersistente &&
    chamada.atendimentoId !== null &&
    chamada.atendimentoId !== Number(atendimento.id)
      ? chamada.atendimentoId
      : null

  useEffect(() => {
    if (!chamadaPersistente || idPessoa === undefined) return
    iniciarChamada({
      idAtendimento: Number(atendimento.id),
      idPessoa,
      isPaciente: false,
      pacienteNome,
      protocolo,
      sexoIdade: sexoIdade || undefined,
    })
  }, [
    chamadaPersistente,
    iniciarChamada,
    atendimento.id,
    idPessoa,
    pacienteNome,
    protocolo,
    sexoIdade,
  ])
  const { height: anamneseHeight, containerRef: anamneseContainerRef } = usePersistedHeight(
    'atendimento-anamnese-editor-altura',
    300,
    180
  )

  function handleNewUser(size: number) {
    setParticipantCount(size + 1)
  }

  const atualizarAnotacao = async () => {
    setLoading(true)
    try {
      const data: AtendimentoDtoAnotacao = {
        tipo: AnotacaoType.Anamnese,
        anotacao,
      }
      await adicionarAnotacao(Number(atendimento?.id), data)

      sessao.atualizar({ anamneseSalva: anotacao })

      showAlert({ title: 'Sucesso', text: 'Anamnese criada com sucesso!', icon: 'success' })
    } catch (error) {
      showError(error)
    } finally {
      setLoading(false)
    }
  }

  //TO-DO: Effect do whereby, depois de testado, REMOVER!
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const data = event.data
      if (data?.type === 'participantupdate' && data.payload?.count !== undefined)
        setParticipantCount(data.payload.count)
    }

    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [])

  const anotacaoNaoSalva = anotacao !== anotacaoSalva

  const modalidadeEspecialidade = [
    atendimento.modalidade ? MODALIDADE_LABEL[atendimento.modalidade] : undefined,
    atendimento.especialidade?.nome,
  ]
    .filter(Boolean)
    .join(' · ')
  const iniciais = (atendimento.paciente?.nome || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase())
    .join('')

  const checkAnamnese = async () => {
    const isEmpty = !anotacaoSalva || anotacaoSalva.trim() === ''

    if (isEmpty) {
      const isConfirmed = await showConfirmAlert({
        title: 'Anamnese não preenchida',
        text: 'A anamnese ainda não foi preenchida. Você deseja continuar mesmo assim?',
        icon: 'warning',
        confirmButtonText: 'Sim',
      })
      return isConfirmed
    }

    if (anotacaoNaoSalva) {
      const isConfirmed = await showConfirmAlert({
        title: 'Anamnese não salva',
        text: 'Você fez alterações na anamnese que ainda não foram salvas. Você deseja continuar?',
        icon: 'warning',
        confirmButtonText: 'Sim',
      })
      return isConfirmed
    }

    return true
  }

  return (
    <div className='d-flex flex-column' style={{ minHeight: 'calc(100vh - 120px)' }}>
      {/* Informações adicionais (banner colapsável full-width no topo) */}
      <InformacoesAdicionaisBanner
        instrucoes={atendimento.instrucoes}
        atendimentoId={atendimento.id}
        className='mb-4'
      />

      {/* Aviso de teleconsulta sem Telekit (largura total, abaixo das informações adicionais) */}
      {(((atendimento.modalidade && atendimento.modalidade !== ModalidadeType.Telekit) ||
        participantes === 2) && (
        <div className='row d-flex align-items-center mb-4'>
          {atendimento.modalidade && atendimento.modalidade !== ModalidadeType.Telekit && (
            <div className='col-12'>
              <InfoContainer className='w-100 border border-info'>
                <span className='flex-grow-1'>
                  Esta teleconsulta está sendo realizada sem o uso do Telekit.A captura de alguns
                  dispositivos pode não estar disponível.
                </span>
              </InfoContainer>
            </div>
          )}
        </div>
      )) ||
        null}

      {/* Corpo: vídeo + anamnese | trilho de ações */}
      <div className='row g-4 flex-grow-1'>
        <div className='col-12 col-md d-flex flex-column' style={{ minWidth: 0 }}>
          {/* Cabeçalho do atendimento (na coluna do vídeo) */}
          <div className='card border shadow-sm mb-4' style={{ minHeight: '7rem' }}>
            <div className='card-body py-3 px-4 d-flex flex-wrap align-items-center gap-3'>
              <div className={styles.headerAvatar}>{iniciais}</div>
              <div className='d-flex flex-column' style={{ minWidth: 0 }}>
                <div className='d-flex align-items-center gap-3' style={{ minWidth: 0 }}>
                  <span className={`text-truncate fs-6 fw-bold lh-sm ${styles.textEmphasis}`}>
                    {atendimento.paciente?.nome}
                  </span>
                  {atendimento.paciente?.id && (
                    <a
                      href={`/apps/atendimentosMedicos/historicoClinico/${atendimento.paciente.id}`}
                      target='_blank'
                      rel='noreferrer'
                      className={`flex-shrink-0 ${styles.historicoLink}`}
                    >
                      <KTSVG path={IconPaths.History} className='svg-icon-em fs-4' ariaHidden />
                      Ver histórico clínico
                    </a>
                  )}
                </div>
                <div className='d-flex flex-wrap align-items-center gap-2 mt-1'>
                  {sexoIdade && <span className='badge badge-light-primary'>{sexoIdade}</span>}
                  {modalidadeEspecialidade && (
                    <span className='badge badge-light-primary'>{modalidadeEspecialidade}</span>
                  )}
                </div>
              </div>
              <div className='ms-auto d-flex flex-column gap-2 align-items-end'>
                {atendimento.estado?.descricao && (
                  <span className={`badge px-3 py-2 fw-semibold ${styles.statusBadge}`}>
                    {atendimento.estado.descricao}
                  </span>
                )}
                <div className='d-flex flex-wrap align-items-center gap-3 gap-md-4 justify-content-start justify-content-md-end'>
                  {atendimento.protocolo && (
                    <div className='d-flex flex-column text-end'>
                      <span className='text-muted text-uppercase fs-8 fw-bold'>Protocolo</span>
                      <span className='fs-7 fw-bold text-gray-800'>{atendimento.protocolo}</span>
                    </div>
                  )}
                  {atendimento.inicioFormatado && (
                    <div className='d-flex flex-column text-end'>
                      <span className='text-muted text-uppercase fs-8 fw-bold'>Início</span>
                      <span className='fs-7 fw-bold text-gray-800'>
                        {atendimento.inicioFormatado}
                      </span>
                    </div>
                  )}
                  {atendimento.inicio && (
                    <TempoDecorrido inicio={atendimento.inicio} atendimento={atendimento} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='d-flex flex-column w-100 flex-grow-1 mb-4' style={{ minHeight: 360 }}>
            {/*Depois que o video estiver validadao, remover essas verificações e deixar apenas o AGORA*/}
            <div className='w-100 h-100 overflow-hidden' style={{ borderRadius: '0.75rem' }}>
              {chamadaPersistente ? (
                chamadaAtivaDeOutroAtendimento !== null ? (
                  <InfoAlert variant='warning' className='m-5'>
                    {t('atendimento.chamada.outra_ativa')}{' '}
                    <Link to={rotaDaChamada(chamadaAtivaDeOutroAtendimento)} className='fw-bold'>
                      {t('atendimento.chamada.ir_para_ativa')}
                    </Link>
                  </InfoAlert>
                ) : (
                  <EncaixeChamada />
                )
              ) : videoAgoraHabilitado() ? (
                <VideoAtendimentoAgora
                  idAtendimento={Number(atendimento.id)}
                  idPessoa={currentUser!.pessoa!.id}
                  isPaciente={false}
                  pacienteNome={atendimento.paciente?.nome}
                  handleNewUser={handleNewUser}
                  chatOpen={chatOpen}
                  setChatOpen={setChatOpen}
                />
              ) : (
                <iframe
                  title='Video Chamada'
                  ref={iframeRef}
                  className='d-block w-100 h-100'
                  src={`${toAbsoluteUrl(atendimento.urlSalaVideo as string)}&externalId=${
                    currentUser?.id
                  }`}
                  allow='camera; microphone; fullscreen; speaker; display-capture; autoplay'
                ></iframe>
              )}
            </div>
          </div>

          <div className='card border shadow-sm'>
            <div className='card-header border-0 d-flex align-items-center'>
              <h3 className='card-title align-items-center gap-2'>
                <span className='fw-bold fs-5'>Registro de Anamnese</span>
                {anotacaoNaoSalva && (
                  <span className={`badge ${styles.badgeAlerta}`}>Não salvo</span>
                )}
              </h3>
            </div>
            <div className={`card-body pt-0 ${styles.anamneseEditor}`}>
              <div
                ref={anamneseContainerRef}
                className={styles.anamneseResize}
                style={{ height: anamneseHeight }}
              >
                <TextEditor value={anotacao} onChange={setAnotacao} height='100%' />
              </div>
            </div>
            <div className='card-footer border-0 d-flex justify-content-end pt-0'>
              <Button
                className='btn-primary'
                icon={IconPaths.Check}
                disabled={loading}
                onClick={atualizarAnotacao}
              >
                {loading ? <LoadingIndicator /> : 'Salvar Anamnese'}
              </Button>
            </div>
          </div>
        </div>

        <div className='col-12 col-md-5 col-lg-4 col-xxl-3'>
          <AsideMenuAtendimentoMain
            atendimento={atendimento}
            onFinalizar={checkAnamnese}
            temAlteracoesNaoSalvas={anotacaoNaoSalva}
          />
        </div>

        {chatOpen && (
          <div className='col-12 col-lg-3' style={{ maxWidth: 360 }}>
            <div
              id='atendimento-chat-container'
              className='d-flex flex-column w-100'
              style={{ height: 'calc(100vh - 150px)', minHeight: 360 }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
